/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type {
  JobTeaserVisibleJobFragment,
  DreamMachineTakenJobFragment,
} from '@xing-com/crate-jobs-graphql';

import {
  isValidSalary,
  isValidSalaryEstimate,
  isValidSalaryRange,
} from './salary';

const NOT_BOOKMARKED = 'NOT_BOOKMARKED';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectAvailableJobData = (job: JobTeaserVisibleJobFragment) => {
  const {
    companyInfo,
    salary,
    activatedAt,
    refreshedAt,
    title,
    location,
    redirectsToThirdPartyUrl,
  } = job;

  const companyName = companyInfo?.companyNameOverride;
  const companyLogo = companyInfo?.company?.logos?.x1 || '';
  const ratingAverage = companyInfo?.company?.kununuData?.ratingAverage;
  const employmentTypeValue = job.employmentType?.localizationValue;
  const bookmarkState = job.userInteractions?.bookmark?.state ?? NOT_BOOKMARKED;
  const isBookmarked = bookmarkState !== NOT_BOOKMARKED;

  const showSalary =
    isValidSalary(salary) ||
    isValidSalaryRange(salary) ||
    isValidSalaryEstimate(salary);

  return {
    salary,
    showSalary,
    activatedAt,
    refreshedAt,
    title,
    location,
    companyName,
    companyLogo,
    ratingAverage,
    employmentTypeValue,
    isBookmarked,
    redirectsToThirdPartyUrl,
  };
};

const selectTakenJobData = (
  jobData: DreamMachineTakenJobFragment
): ReturnType<typeof selectAvailableJobData> => {
  const job = jobData?.job;

  const showSalary =
    isValidSalary(job?.salary) ||
    isValidSalaryRange(job?.salary) ||
    isValidSalaryEstimate(job?.salary);

  return {
    salary: jobData?.job?.salary,
    showSalary: showSalary,
    activatedAt: '',
    refreshedAt: '',
    title: job?.jobTitle || '',
    location: {
      city: job?.company?.address?.city || job?.fallbackCompany?.address?.city,
    },
    companyName:
      job?.company?.companyName || job?.fallbackCompany?.companyName || '',
    companyLogo: job?.company?.logos?.logo256px || '',
    ratingAverage: job?.company?.kununuData?.ratingAverage,
    employmentTypeValue: undefined,
    isBookmarked: false,
    redirectsToThirdPartyUrl: false,
  };
};

export const selectJobData = (
  job: JobTeaserVisibleJobFragment | DreamMachineTakenJobFragment
): ReturnType<typeof selectAvailableJobData> => {
  if (job.__typename === 'TakenJob') {
    return selectTakenJobData(job);
  }
  if (job.__typename === 'VisibleJob') {
    return selectAvailableJobData(job);
  }

  // Return a default object with safe fallback values
  return {
    salary: undefined,
    showSalary: false,
    activatedAt: '',
    refreshedAt: '',
    title: '',
    location: {},
    companyName: '',
    companyLogo: '',
    ratingAverage: undefined,
    employmentTypeValue: undefined,
    isBookmarked: false,
    redirectsToThirdPartyUrl: false,
  };
};
